import type { EnrichedRoom, Room } from '../types/rooms'
import { extensionUuidKey } from './extensionUuid'

interface ExtensionData {
  id: number
  extension_uuid: string
  number: string
  name: string
}

/**
 * Derive a stable, unique, negative pseudo-id for a room whose extension
 * couldn't be matched. Negative keeps the existing `room.id > 0` guards (e.g.
 * voicemail prefetch) treating it as "no real extension", while a per-uuid
 * hash keeps selection/map keys distinct so unmatched rooms don't collide on
 * `id: 0` in `Set<number>` selection or `Map<number, room>` lookups.
 */
const unmatchedRoomId = (room: Room): number => {
  const key = room.extension_uuid || room.uuid || ''
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0
  }
  // Map to a negative range, avoiding 0 even for empty keys.
  return -(Math.abs(hash) + 1)
}

/**
 * Enrich a room with extension data (id, number, name) and voicemail count.
 * Used when the proxy API returns slim responses without extension details.
 */
export const enrichRoom = (
  room: Room,
  extensionMap: Map<string, ExtensionData>,
  voicemailCounts?: Map<string, number>,
): EnrichedRoom => {
  if (
    room.id !== undefined &&
    room.number !== undefined &&
    room.name !== undefined &&
    room.hospitality !== undefined
  ) {
    return room as EnrichedRoom
  }

  const ext = extensionMap.get(extensionUuidKey(room.extension_uuid))

  return {
    ...room,
    id: ext?.id ?? unmatchedRoomId(room),
    number: ext?.number ?? '',
    name: ext?.name ?? '',
    hospitality: true,
    voicemail_count: voicemailCounts?.get(room.extension_uuid) ?? null,
  }
}

const roomNumberCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

/**
 * Order rooms by extension number ascending ("2" before "10"). Rooms without
 * a matched extension number go last.
 */
const compareRoomsByNumber = (a: EnrichedRoom, b: EnrichedRoom): number => {
  if (!a.number || !b.number) {
    return Number(!a.number) - Number(!b.number)
  }
  return roomNumberCollator.compare(a.number, b.number)
}

/**
 * Enrich a list of rooms with extension data, ordered by room number.
 */
export const enrichRooms = (
  rooms: Room[],
  extensionMap: Map<string, ExtensionData>,
  voicemailCounts?: Map<string, number>,
): EnrichedRoom[] => {
  return rooms
    .map((room) => enrichRoom(room, extensionMap, voicemailCounts))
    .sort(compareRoomsByNumber)
}

/**
 * Build an extension map from extension-lines data for quick lookup by extension_uuid.
 */
export const buildRoomExtensionMap = (
  extensions: Array<{
    id: number
    extension_uuid: string
    number: string
    name: string
  }>,
): Map<string, ExtensionData> => {
  const map = new Map<string, ExtensionData>()
  for (const ext of extensions) {
    map.set(extensionUuidKey(ext.extension_uuid), ext)
  }
  return map
}
